import { useQuery } from "react-query";
import { useEffect, useRef, useState } from "react";

import { $ok, $totype, $UUID } from "foundation-ts/commons";
import { TSDate } from "foundation-ts/tsdate";
import { Resp } from "foundation-ts/tsrequest";
import { Nullable } from "foundation-ts/types";

import apiClient from "@services/apiClient";

import { InfosAfterSignupID } from "./hooksConstants";
import { RegisteredInformationDto } from "doxecure-commons/doxecureClientTypes";

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 10000;

const useFetchRegisteredInformation = (corporationId: Nullable<string>) => {
    const [remainingSeconds, setRemainingSeconds] = useState(POLL_TIMEOUT_MS / 1000);
    const [isTimedOut, setIsTimedOut] = useState(false);
    const pollStartTime = useRef<number|null>(null);
    const isResolvedRef = useRef(false);

    const query = useQuery(
        [InfosAfterSignupID, corporationId],
        async () => {
            const [resp, status] = await apiClient.getRegisteredInformation($UUID(corporationId)!);
            if (status !== Resp.OK) { return null; }
            const data = $totype<RegisteredInformationDto>(resp);
            isResolvedRef.current = $ok(data?.corporation.initialFormData?.paymentStatus);
            return data;
        },
        {
            enabled: Boolean(corporationId),
            cacheTime: 0,
            staleTime: 0,
            refetchInterval: (data) => {
                if ($ok(data?.corporation.initialFormData?.paymentStatus)) { 
                    isResolvedRef.current = true;
                    return false;
                }

                if (isTimedOut) { return false; }

                return POLL_INTERVAL_MS;
            },
            refetchIntervalInBackground: false
        }
    );

    useEffect(() => {
        if (!$ok(corporationId)) { return; }

        isResolvedRef.current = false;
        pollStartTime.current = TSDate.zulu().timestamp;
        setRemainingSeconds(POLL_TIMEOUT_MS / 1000);
        setIsTimedOut(false);

        const timer = setInterval(() => {
            if (isResolvedRef.current) {
                clearInterval(timer);
                return;
            }

            const elapsed = TSDate.zulu().timestamp - pollStartTime.current!;
            const remaining = Math.max(0, POLL_TIMEOUT_MS/1000 - elapsed);
            setRemainingSeconds(remaining);

            if (remaining === 0) {
                setIsTimedOut(true);
                clearInterval(timer);
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [corporationId]);

    return {
        ...query,
        remainingSeconds,
        isResolved: isResolvedRef.current,
        isTimedOut,
    };
};

export default useFetchRegisteredInformation ;